import { rulesBlock } from './expertRulesStore';
// The three kinds a sub-step may be, listed under their two families. Imported rather than
// written out here: the same list is what the app stores and what the tables print, and a
// prompt describing a sixth kind — or four of the five — is a prompt asking for answers
// nothing downstream can hold.
import { MADE_ID_BASE, subStepKindLines } from './subStepKinds';

// The FULL Phase 4 (step-analyze) prompt, authored HERE on the client — instructions, the
// parts context and the JSON output format — so the badge beside the run button can show
// EVERY word of it and the client can send that exact string to the server, which uses it
// verbatim. One source of truth: the preview and the payload are the same string, so they
// can never drift.
//
// Keep the JSON shape in TAIL in sync with the server's _parse_step_analyze parser.
//
// It no longer asks which parts the step adds. That is one answer about the whole manual —
// a supplied quantity is shared out between the steps — and it is made once, in Phase 2,
// by a call that holds every step's diagram at the same time. Here it arrives as given,
// in the context block, and the sub-steps below account for exactly those units.

// HOW FAR APART THE BLOCKS STAND, and why it is a constant rather than a habit.
//
// This prompt is read by a person as often as it is sent to a model — it is the whole of what
// the badge beside the run button shows — and as one unbroken column of 11px monospace it is
// a page nobody finds their place in twice.
//
// ONE BLANK ROW, EVERYWHERE. It was three between the main paragraphs and one inside them,
// which put more screen between two sentences than either of them occupied: on a 1400px page
// the four numbered answers no longer fitted together, and a reader scrolling past three
// empty rows is a reader who has lost the thread the spacing was meant to give them. The
// hierarchy is carried by the INDENT — main paragraphs at the margin, their parts three
// spaces in, a hole's fields five — which is what indentation is for, and it costs no height.
//
// Exported because the per-step context is built elsewhere (see stepsRun) and its sections
// are main paragraphs of this same prompt. Two files each spacing "the way the other one
// does" is two files that stop agreeing the first time either is touched.
export const GAP = '\n\n';

// The opening, up to the sentence that says what to return — which is where the one line
// about a first step is inserted when there IS one. See buildStepAnalyzePrompt.
const OPENING =
  'That image is one assembly step of a furniture manual: diagrams with callouts labelling ' +
  'parts by letter or reference number, plus any printed text. The parts this step consumes ' +
  'follow, and the sub-steps must account for exactly those units and no others.';

// THE SUB-STEPS FIRST, IN THE SENTENCE AND IN THE NUMBERING. "buildsOn" is one line of
// integers and it led, which put the whole of what this call is for in second place — and a
// model reads a numbered list as an order of work.
const RETURNS =
  'Return a SINGLE JSON object with keys "subSteps" and "buildsOn":';

// The two numbered answers and the rules under them. The context follows, carrying its own
// headings — it used to be introduced by a "Supplied parts (id -> description):" line here,
// which stopped being true when the whole catalogue stopped being sent: what follows now is
// what earlier steps assembled and what each of them achieved, each saying so. The step's own
// parts and tools are above, in the adds block (see buildStepPromptContext).
//
// IN FOUR PARTS, IN THE ORDER THE WORK IS DONE, Louis 2026-10-22: what a sub-step is, the order
// they come in, what merges, and how pieces are numbered; then the fields. It had grown one rule
// at a time into "one unit at a time, never one sub-step handling all four" followed by four
// exceptions merging units back, and a cap of two parts with carve-outs: the model was left to
// work out which rule won. Now it splits first and merges second, and every merge is in one list.
//
// STATED BY THEIR CONDITIONS, NO EXAMPLE FROM A MANUAL. An example written in the house style of
// the sentences this call produces is the one most likely to be pattern-matched onto a sub-step
// it is not about. The kinds and the merges count pieces, which is what makes them applicable
// without a specimen. The history of each rule is with it below.
const head = (nextPiece: number) =>
  // THE STEP'S TOOLS AND ITS PRINTED TEXT ARE NOT ASKED FOR HERE. Which tools the step calls for
  // is Phase 3's (the reader who sees every step at once), and arrives in the adds block; the
  // transcription is Phase 3's own call too (see stepInstructionsRun). Which of the step's tools
  // each GESTURE picks up stays: only this call is looking at one gesture.
  '1) "subSteps": the step as an ordered sequence of sub-steps, in the order performed. Work ' +
  'it out in two passes: first split the step into single gestures, then merge the ones the ' +
  'merges below allow, and only those.' + GAP +
  // THE KINDS ARE THE DEFINITION OF "ATOMIC". "Attach the side rail (C) to the frame (A) using 2
  // screws and 2 washers, with the Allen key" ends in a joined state, so no rule about joined
  // states could cut it. The kinds count their pieces, so an action carrying more than one kind's
  // worth is more than one sub-step. The list lives in subStepKinds, with the app that stores it.
  '   SPLIT. Every gesture is exactly one of these kinds, given as "kind". An action that does ' +
  'not fit exactly one of them is more than one gesture: split it.\n' +
  subStepKindLines() + GAP +
  // THE NUMBERS COUNT UNITS: "insert the barrel nuts (G x2) into a side rail (C x1)" came back as
  // ONE kind 3. And A TOOL IS NOT A PART (2026-10-17): a fastener put in and driven straight after
  // is one gesture naming its tool, where it was two and "Tighten the screw" never said with what.
  '   Those numbers count UNITS, not sorts of part: a gesture takes ONE component part and ONE ' +
  'hardware part, or TWO component parts, never three parts. A TOOL IS NOT A PART: it goes with ' +
  'the gesture it is used in, so putting a fastener in and driving, tightening or adjusting it ' +
  'with its tool is ONE gesture. Where the step does the same thing to several units of an ' +
  'identical part, it is one gesture per unit, each with its own quantity of one unless a single ' +
  'motion genuinely takes several.' + GAP +
  // ONE PART MOVED AND JOINED TO NOTHING ("lay the frame flat") had a kind and does not any more
  // (see subStepKinds); without this line the rule above sends the model to SPLIT it. EXCLUDED,
  // NOT MERGED: folding it into the next gesture asks for a sentence describing two things. And a
  // tool turned on its own, since the fourth kind went: a later pass of the key joins nothing.
  '   Moving or turning ONE part and joining it to nothing is not a gesture: leave it out. ' +
  'Neither is turning a tool on a fastener already in place, with nothing put in: the gesture ' +
  'that put that fastener in already names its tool.' + GAP +
  // ORDER, AND BOTH RULES SIDE BY SIDE: each is the other's exception.
  //
  // ONE UNIT AT A TIME, AND NO CYCLES. A run used to carry a shared "cycle" and a "times", so three
  // gestures over four rails were three rows standing for twelve, and every number had to declare
  // which frame it was in. Written out, a gesture is a gesture and a quantity is a quantity. And
  // one unit DONE before the next is started, Louis 2026-10-22: a reader does one side, then the
  // other.
  //
  // INTO A LOOSE PART FIRST, Louis 2026-10-21: a side rail was held against the frame and only then
  // were its barrel nuts put in, one hand holding, the other turning a nut it could hardly see.
  // ONLY A LOOSE PART, 2026-10-22: read broadly it put every barrel nut first, two gestures away
  // from the screw that turns into it. In a part already fixed in a piece the nut's slot is lined
  // up for its screw, so it goes in right before it.
  '   ORDER. Where the step does the same thing to several units, finish each unit, every ' +
  'gesture on it, before starting the next. Hardware that goes into a hole of ONE part only, ' +
  'without passing through to another, goes into that part while the part is still LOOSE, ' +
  'before it is positioned against another one. Hardware a later fastener screws into, going ' +
  'into a part ALREADY fixed in a piece, goes in right before the gesture that puts that ' +
  'fastener in, after any part the fastener goes through has been positioned.' + GAP +
  // THE MERGES, ONE LIST, EACH RULE A CONDITION ON CONSECUTIVE GESTURES. Each was two screens for
  // what a reader's hands do in one go:
  //   prepared in the hand (Louis 2026-10-21): the washer on, then the screw in (Slot.prepares);
  //   prepared into a loose part (2026-10-21): the nuts into the rail, then the rail against the
  //     frame; Phase 6 plays the preparation first, a beat each;
  //   covers (2026-10-17): four felt pads under four feet (fromHole, coverOf);
  //   hardware in a run (2026-10-20): two dowels into one rail, four screws into a seat; on any
  //     face (10-21), a screw each side of a frame; into parts of one piece (10-22), a nut into
  //     each rail of a backrest. Phase 6 puts every unit in its own hole (fromHole, toolHops). No final
  //     tightening anywhere (Louis 2026-10-22): a screen of its own showed nothing Phase 6 can draw;
  //   two hardware in turn (2026-10-21): a barrel nut then dowels into one rail (Fitting.nextOn);
  //     or a fastener into the hardware put in just before (10-22): one side of a frame done;
  //     and a cover over that fastener, third (10-22): the wood button over the JCBC screw just
  //     driven into its barrel nut (Fitting.lastOn).
  '   MERGE. Each of these joins gestures that come ONE STRAIGHT AFTER ANOTHER, with nothing ' +
  'else between them, into ONE sub-step. No other gestures merge.\n' +
  '     a) PREPARED IN THE HAND. Hardware put onto other hardware before that hardware goes in ' +
  'joins the gesture that puts it in, as "prepared", as many units as the hardware it goes onto.\n' +
  '     b) PREPARED INTO A LOOSE PART. Hardware put into a loose part joins the gesture that ' +
  'positions that part against another one, as "prepared", with its tool.\n' +
  '     c) COVERS. A cover is a part put on only to cap, hide or protect, holding nothing ' +
  'together. Units of the SAME cover put on one each are one sub-step putting them all on.\n' +
  '     d) HARDWARE IN A RUN. Units of the SAME hardware (with the same hardware prepared on it, ' +
  'if any), put in one each with the SAME tool or none, into holes of the SAME part (the same ' +
  'unit, where it is one of a pair) or of parts all fixed in the SAME piece, on any face, are ' +
  'one sub-step putting them all in.\n' +
  '     A FINAL TIGHTENING (fasteners tightened once they are all in) is NOT a sub-step and is ' +
  'said NOWHERE: no sub-step, no sentence, no beat. A fastener is driven in, never "left loose".\n' +
  '     e) TWO HARDWARE IN TURN. Two DIFFERENT hardware put into holes of the SAME part (the same ' +
  'unit, where it is one of a pair), or a fastener put into the hardware the gesture before put ' +
  'into a part fixed in the same piece, are one sub-step: both "added", in the order they go in, ' +
  'each with its own "qty", the part once as "receiving", the tools of both. Never more than ' +
  'two, and never a component part among them, but for ONE COVER put on right after over the ' +
  'fastener just put in: it joins too, "added" last, a third.\n' +
  // A RUN IS COVERED AFTER IT, Louis 2026-10-22: with the cover allowed in, the model put a
  // button on each screw in turn (screw, button, screw, button), which broke d)'s run of screws
  // and split 1-2 into one sub-step per hole.
  '     A RUN IS PUT IN WHOLE, THEN COVERED: every unit of it goes in before any cover goes on, ' +
  'never a cover between two units. Its covers then join it as in e).\n' +
  // MERGES COMBINE, Louis 2026-10-22: a barrel nut, then a screw with its washer turned into it,
  // stayed two sub-steps, the model reasoning "because of the washer's position between them": e)
  // said never more than two hardware, and nothing said a washer on the screw was not a third.
  '     Hardware prepared onto another (a) travels with that hardware: it is not one of the two ' +
  'in e), and it is not something between two gestures. Merges combine: a gesture already ' +
  'merged by a) can still be merged by e).\n' +
  '     Merged hardware is said in the sentence in the order it goes in, prepared hardware FIRST. ' +
  'It leaves no piece of its own, and its "qty" is the number of units the sub-step puts in.' +
  GAP +
  // EVERY GESTURE LEAVES A PIECE, AND THE PIECE HAS AN ID. A placing too: a rail held against a
  // frame is one object. It had no id, and a later gesture named it by the ids it was made FROM,
  // each "consumed": false; that field is no longer asked, as every entry is consumed (absent
  // reads true, see _consumes). IN THE HUNDREDS, numbered across the WHOLE manual: a letter read as
  // one of the manual's own callouts, and 101 restarting in each step named a different piece in
  // every one.
  `   PIECES. Every sub-step takes in every part it names and leaves ONE piece holding them, a ` +
  `placing included: a part held against another is one object. Each piece gets an id, in ONE ` +
  `sequence running through the whole manual: the first piece THIS step assembles is ` +
  `${nextPiece}, the next ${nextPiece + 1}, and so on. A later sub-step names that piece by its ` +
  'id, never the parts inside it: naming them again counts them twice.' + GAP +
  '   For each sub-step give:' + GAP +
  // A POSITION, NOT A LETTER: written one unit at a time a step runs past 26 gestures, "aa" sorted
  // between "a" and "b", and every piece id pointed at something else. The label a reader sees is
  // made from this number (see _sub_step_number).
  '   - "at": where this sub-step comes in the step, counting from 1.' + GAP +
  '   - "kind": the number of the kind above that it is (for a merged sub-step, the kind of the ' +
  'gesture it was merged into).' + GAP +
  // THE SENTENCE IS A TEMPLATE OVER "parts". Written as prose with callouts it named parts no
  // column listed, and nothing said so; as tokens, it cannot be composed until the list exists.
  // How a callout is spelt is the app's (see subStepText). FRENCH FIRST, from the drawing: made
  // from the English it came back as English in French words. SAID ONCE, Louis 2026-10-21.
  '   - "textFr" and "textEn": one line describing the action in each language. Write textFr ' +
  'FIRST, from the drawing, as a French furniture manual would say it — its own word order ' +
  'and prepositions — then textEn the same way. Neither is a translation of the other. ' +
  'WRITE FOR SOMEBODY ASSEMBLING IT AT HOME, not for a workshop: everyday words over trade ' +
  'terms — "trou" over "logement", "trou fileté" over "taraudage", "hole" over "bore" — and ' +
  'the shortest sentence that says the gesture. ' +
  'NAME EACH PART ONCE: after its first mention, say "it"/"them" ("la"/"les"), never its id ' +
  'again; and say where a hole is only as far as a reader needs to find it. ' +
  'NAME EVERY PART BY ITS ID IN BRACES and never in words: "Attach {8} to {0} by ' +
  'inserting {101}." — where 8 and 0 are ids from the lists above and 101 is a piece an ' +
  'earlier sub-step left. The app writes the name, the diagram letter and the count in when it ' +
  'draws the sentence, from "parts" below, so give the id alone and no count. Anything you put ' +
  'in braces MUST have an entry in "parts" or "tools", or be a supplied part inside a piece ' +
  'that has one. Words around the tokens are yours — the verb, where a part goes — and the ' +
  'tokens are the only place a part is named.' + GAP +
  // THE TOOL IN THE SENTENCE: "Tighten the JCBC screw (E)" left the reader to look across for it.
  '     NAME THE TOOL TOO, in braces, in every sub-step that uses one: "…then tighten it with ' +
  'the {9}." — its id from "tools" below.' + GAP +
  // THE PART, NOT THE PIECE: a piece is drawn as its base part (see pieceBases), and "the
  // cross-hole of {102}" came out as the frame's when the hole is in the rail.
  '     NAME THE PART WHERE THE GESTURE HAPPENS. When the action is on one part inside a piece ' +
  '— a hole in it, a face of it, its end — put THAT supplied part\'s id in braces, not the ' +
  'piece\'s: the reader holds the piece and looks at that part. "parts" still lists the piece. ' +
  'Put a piece\'s id in braces only where the sentence means the piece as a whole.' + GAP +
  // THE ARTICLE IS THE MODEL'S: the app cannot know a vis is feminine, nor write "du" for "de le".
  '     PUT THE ARTICLE BEFORE EVERY TOKEN, as you would before the noun, in both languages: ' +
  '"Slide the {6} onto the {5}." / "Enfilez la {6} sur la {5}.", "le premier trou du {0}", ' +
  '"dans l\'{7}". The French article agrees with the part\'s French name, given as [fr: …] ' +
  'where it differs. A PIECE in braces is drawn as the supplied part it was built on — the ' +
  'part that received the first gesture making it (listed as "drawn in a sentence as") — so ' +
  'its article is that part\'s. Where its entry takes MORE THAN ONE, the article and every ' +
  'word agreeing with it are plural, as the app draws the name plural: "Enfilez les {6} sur les ' +
  '{5}", "Slide the {6} onto the {5}, then drive them".' + GAP +
  // ITS NAME, which the kinds cannot give. AND NO TWO ALIKE: a chair with a seat screwed on came
  // back "assembled chair", the name it had without the seat. Say what it gained.
  '   - "output": the piece this sub-step LEAVES, named in both languages as {"en": …, "fr": …}: ' +
  'a short noun a later sub-step can pick up. Every piece\'s name is DIFFERENT from every other ' +
  'piece\'s in this manual, including the ones earlier steps left (listed below). A piece ' +
  'assembled ONTO an earlier piece is not that piece: name it by what it has gained.' + GAP +
  // THE MOVEMENT IS NOT ASKED HERE ANY MORE: Phase 5 asks it of ONE sub-step, with its sentence
  // in front of it (see subStepFittingPrompt).
  '   - "parts": the parts this sub-step handles, as [{"id", "qty", "role"}]. Every id is from a ' +
  'list given here — what this step consumes, or what is already assembled — or is a piece an ' +
  'earlier sub-step left, in this step or an earlier one (listed below). Nothing else may be ' +
  'named. List EVERY part the action touches, including one merely moved or already in place, ' +
  'and every part named in the text.\n' +
  '     "qty": the units of it this sub-step takes in.\n' +
  // WHICH PART MOVES, asked of the call that wrote the sentence. Two ends to every fitting.
  '     "role": "added" for the part this gesture brings and puts onto, into or against ' +
  'something — the part that MOVES — and "receiving" for the part it is put onto, which stays ' +
  'where it is; "prepared" and two "added" only as the merges above say. A tool has none and is ' +
  'not listed here.\n' +
  // WHICH OF A PAIR, ON EVERY ENTRY OF ONE AND OF A PIECE HOLDING ONE: two gestures fitting both
  // rails read identically, a seat's screws into a piece holding both rails reached no right-hand
  // holes, and the hardware table's rows are matched by this word (see drillGroupParts).
  '     "side": "left" or "right", ON EVERY ENTRY of a part the list above marks as a ' +
  'LEFT/RIGHT mirror pair, AND on every entry naming a piece with such a part inside it, ' +
  'whenever the gesture works on one of the two units, including a piece that holds both ' +
  'units where the gesture goes into or through only one of them. The side is that unit\'s: ' +
  'the one on the left of the furniture as the finished piece faces you, or the one on the ' +
  'right. Read it off where the unit stands in the diagram. Where a step does the same thing ' +
  'to both units one after the other, the gestures on each unit carry that unit\'s side, so no ' +
  'two of them read alike; where the sentence names the unit\'s side, the entry says it too. ' +
  'On no other entry.\n' +
  '     CRITICAL: for each part, the "qty" of all its entries MUST add up to its total in ' +
  '"What this step consumes" above. Those totals are given, not to be re-judged.' + GAP +
  // THE SENTENCE IN BEATS, played one at a time under the animation, Louis 2026-10-21.
  '   - "beatsEn", "beatsFr": the same sentence cut into the moments done in turn, as a list: ' +
  'where the sub-step has "prepared" hardware, the preparation alone first; where it puts two ' +
  'hardware in turn, each its own, in order; otherwise ONE, the sentence itself. Each one a ' +
  'whole short sentence of its own, naming its parts in braces like the sentence.' + GAP +
  '   - "tools": the tools this sub-step uses, as [{"id"}], taken from the tools listed for ' +
  'this step above; a tool may serve several.' + GAP +
  '2) "buildsOn": the NUMBERS of the earlier steps whose sub-assemblies appear in this ' +
  'diagram — the ones the new parts attach to or act on. Earlier steps are listed below ' +
  'with what each produced. Integers only; [] when there are none.';

// The JSON output format, appended after the context.
const tail = () =>
  GAP +
  'Reply with ONLY a JSON object, no prose, no code fence, using the ids given above:\n' +
  '{"subSteps": [{"at": <integer ≥ 1>, "kind": <one of the kinds above>, ' +
  '"textFr": <string>, "textEn": <string>, "beatsFr": [<string>], "beatsEn": [<string>], ' +
  '"output": {"en": <string>, "fr": <string>} or null, ' +
  '"parts": [{"id": <id string>, "qty": <integer, units this sub-step takes in>, ' +
  '"role": <"added"|"receiving"|"prepared">, ' +
  '"side": <"left"|"right", only on a mirror pair>}], ' +
  '"tools": [{"id": <id string>}]}], ' +
  '"buildsOn": [<earlier step number>]}.';

// Assemble the whole prompt exactly as it is sent: instructions, then the parts/assembled/
// previous context, then the JSON format. `context` is buildStepPromptContext's output.
//
// THE VIDEO STYLE GUIDANCE IS GONE. It rode along on every step-analyze call — a paragraph
// telling the model that each sub-step's description feeds a video generator, and what such
// a description should mention. This call is not writing a video prompt: it breaks a step
// into physical actions, and the instructions above already say what a sub-step's sentence
// must name, in the same words and at less length.
//
// It came from descriptionsRun, a module holding one constant and a function returning it —
// once the editable template of a video phase, latterly a fixed paragraph nobody could
// change. Nothing else read it, so it is deleted rather than left as the last caller of a
// file about a feature that is not here.
//
// `firstStep` says nothing has been assembled yet. It is a line of the OPENING and not of the
// context, though it is a fact about the context: "there is nothing behind this step" belongs
// with what the step IS, next to the sentence that says what to answer — where it was, at the
// far end of the prompt under a heading about earlier steps, it read as the absence of a list
// rather than as the reason there is none.
// `nextPiece` is the number this step's first piece takes — the count carries on from the
// steps before it rather than restarting here. 1 for the first step of a manual, and 1 as a
// default for a caller with nothing to continue.
export function buildStepAnalyzePrompt(
  { adds, context }: { adds: string; context: string },
  firstStep = false,
  // The number this step's first assembled piece takes — the count carries on from the steps
  // before it, exactly as the cycle numbering does. See nextPieceNo.
  nextPiece = MADE_ID_BASE,
): string {
  const opening = firstStep
    ? `${OPENING} This is the first step; nothing has been assembled yet.`
    : OPENING;
  // THE TOTALS DIRECTLY UNDER THE RULE ABOUT THEM. "The sub-steps must account for exactly
  // those units and no others" was the last sentence of the opening and the list it is about
  // was four screens down, among the catalogue and the earlier steps. They are one thought.
  // The context is joined rather than glued: it is one or two sections now, either of which
  // can be absent — a first step has nothing assembled behind it and no earlier step to name —
  // and an empty one used to leave its blank rows behind.
  // AND THE EXPERT RULES SWITCHED ON (see expertRulesStore), after the rules of the answer and before
  // what this step is made of: they are about how to split it, as those are.
  const rules = rulesBlock();
  return opening + GAP + adds + GAP + RETURNS + GAP + head(nextPiece) +
    (rules ? GAP + rules : '') + (context ? GAP + context : '') + tail();
}
